import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@mui/material";
import { PersonAdd, PersonRemove, Check } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import FollowService from "../../services/FollowService";

/**
 * FollowButton — universal follow/unfollow button.
 * Props:
 *   targetUserId  — who to follow
 *   initialIsFollowing — (optional) skip the check API call if parent knows
 *   size          — "small" | "medium" (default: "small")
 *   variant       — "contained" | "outlined" (default: "outlined")
 *   onFollowChange — callback(isFollowing: bool)
 */
const FollowButton = ({
  targetUserId,
  initialIsFollowing,
  size = "small",
  variant = "outlined",
  onFollowChange,
}) => {
  const { user } = useSelector((state) => state.auth);
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing ?? null);
  const [isHovering, setIsHovering] = useState(false);
  const [loading, setLoading] = useState(initialIsFollowing === undefined);

  // Don't render for self
  const isSelf = user && (user.id === targetUserId || user._id === targetUserId);

  useEffect(() => {
    if (isSelf || !user || !targetUserId) return;
    if (initialIsFollowing !== undefined) {
      setIsFollowing(initialIsFollowing);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await FollowService.checkFollow(targetUserId);
        if (!cancelled) setIsFollowing(res?.data?.isFollowing ?? false);
      } catch {
        if (!cancelled) setIsFollowing(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [targetUserId, user, isSelf, initialIsFollowing]);

  if (isSelf || !user) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    const prev = isFollowing;
    setIsFollowing(!prev); // optimistic
    try {
      if (prev) {
        await FollowService.unfollowUser(targetUserId);
      } else {
        await FollowService.followUser(targetUserId);
      }
      onFollowChange?.(!prev);
    } catch {
      setIsFollowing(prev); // rollback
    }
  };

  if (loading) {
    return (
      <Button
        size={size}
        disabled
        sx={{ minWidth: 90, borderRadius: "20px", textTransform: "none" }}
      >
        <CircularProgress size={14} />
      </Button>
    );
  }

  if (isFollowing) {
    return (
      <motion.div whileTap={{ scale: 0.95 }}>
        <Button
          size={size}
          variant="outlined"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={handleClick}
          startIcon={
            isHovering
              ? <PersonRemove sx={{ fontSize: 15 }} />
              : <Check sx={{ fontSize: 15 }} />
          }
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            minWidth: 100,
            px: 2,
            borderColor: isHovering
              ? "rgba(239, 68, 68, 0.5)"
              : "color-mix(in srgb, var(--text-primary) 15%, transparent)",
            color: isHovering
              ? "#ef4444"
              : "color-mix(in srgb, var(--text-primary) 70%, transparent)",
            "&:hover": {
              borderColor: "rgba(239, 68, 68, 0.6)",
              background: "rgba(239, 68, 68, 0.06)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {isHovering ? "Unfollow" : "Following"}
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div whileTap={{ scale: 0.95 }}>
      <Button
        size={size}
        variant={variant}
        onClick={handleClick}
        startIcon={<PersonAdd sx={{ fontSize: 15 }} />}
        sx={{
          borderRadius: "20px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8rem",
          minWidth: 100,
          px: 2,
          ...(variant === "contained"
            ? {
                background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(59,130,246,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #06b6d4 0%, #1d4ed8 100%)",
                  boxShadow: "0 6px 16px rgba(59,130,246,0.5)",
                },
              }
            : {
                borderColor: "rgba(59, 130, 246, 0.5)",
                color: "#6366f1",
                "&:hover": {
                  borderColor: "#6366f1",
                  background: "rgba(59, 130, 246, 0.08)",
                },
              }),
          transition: "all 0.2s ease",
        }}
      >
        Follow
      </Button>
    </motion.div>
  );
};

export default FollowButton;
